/*
 * SponsorPartnerStrip — reusable logo strip for TeamHubPage + AcademyLandingPage
 * =============================================================================
 * Iter 71 · Shows two rows — Χορηγοι + Συνεργατες — scoped to first_team or academy.
 * Rendered only if there is at least one entry to show, so we don't leave an
 * empty section on brand-new deployments.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SponsorPartnerStrip = ({ scope }) => {
  const [sponsors, setSponsors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/sponsors?type=${scope}`).then(r => r.data).catch(() => []),
      axios.get(`${API}/partners?scope=${scope}`).then(r => r.data).catch(() => []),
    ]).then(([sp, pt]) => {
      if (cancelled) return;
      setSponsors((sp || []).filter(s => s.logo_url));
      setPartners(pt || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope]);

  if (loading) return null;
  if (sponsors.length === 0 && partners.length === 0) return null;

  const sponsorHref = scope === "academy" ? "/sponsors/academy" : "/sponsors/first-team";
  const partnerHref = `/partners?scope=${scope}`;

  return (
    <section className="py-16 px-6 bg-black border-t border-[#1a1a1a]" data-testid={`sp-strip-${scope}`}>
      <div className="max-w-7xl mx-auto space-y-14">
        {/* Sponsors */}
        {sponsors.length > 0 && (
          <div data-testid="sp-strip-sponsors">
            <div className="flex items-end justify-between mb-2">
              <h2 className="font-['Bebas_Neue'] text-3xl text-white tracking-wide">
                ΟΙ <span className="text-[#F5A623]">ΧΟΡΗΓΟΙ</span> ΜΑΣ
              </h2>
              <Link to={sponsorHref} className="text-xs text-[#F5A623] hover:underline">
                ΟΛΟΙ ΟΙ ΧΟΡΗΓΟΙ →
              </Link>
            </div>
            <div className="w-12 h-1 bg-[#F5A623] mb-8" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {sponsors.slice(0, 12).map(s => (
                <Link
                  key={s.id}
                  to={`/sponsors/${s.id}`}
                  className="group bg-[#0f0f0f] border border-[#1e1e1e] rounded-lg p-4 h-24 flex items-center justify-center hover:border-[#F5A623]/40 transition-colors"
                  data-testid={`sp-strip-sponsor-${s.id}`}
                  title={s.name}
                >
                  <img
                    src={s.logo_url}
                    alt={s.name}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain opacity-70 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition duration-300"
                  />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Partners */}
        {partners.length > 0 && (
          <div data-testid="sp-strip-partners">
            <div className="flex items-end justify-between mb-2">
              <h2 className="font-['Bebas_Neue'] text-3xl text-white tracking-wide">
                ΟΙ <span className="text-[#F5A623]">ΣΥΝΕΡΓΑΤΕΣ</span> ΜΑΣ
              </h2>
              <Link to={partnerHref} className="text-xs text-[#F5A623] hover:underline">
                ΟΛΟΙ ΟΙ ΣΥΝΕΡΓΑΤΕΣ →
              </Link>
            </div>
            <div className="w-12 h-1 bg-[#F5A623] mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {partners.slice(0, 6).map(p => (
                <Link
                  key={p.id}
                  to={`/partners/${p.slug}`}
                  className="group bg-[#0f0f0f] border border-[#1e1e1e] rounded-lg p-5 flex items-center gap-4 hover:border-[#F5A623]/40 transition-colors"
                  data-testid={`sp-strip-partner-${p.slug}`}
                >
                  <div className="w-14 h-14 flex-shrink-0 rounded bg-black border border-[#1e1e1e] flex items-center justify-center overflow-hidden">
                    {p.headshot_url || p.hero_image_url ? (
                      <img
                        src={p.headshot_url || p.hero_image_url}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Handshake size={22} className="text-[#F5A623]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-['Bebas_Neue'] text-lg text-white truncate group-hover:text-[#F5A623] transition-colors tracking-wide">
                      {p.name}
                    </p>
                    {p.tagline && (
                      <p className="text-[11px] text-zinc-500 truncate">{p.tagline}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SponsorPartnerStrip;
